import { FC, Fragment, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import * as XLSX from 'sheetjs-style'
import jsPDF from 'jspdf'
import PasswordConfirmModal from './PasswordConfirmModal'
import { ConferenceLoadingOverlay } from './ConferenceLoadingOverlay'
import useAuth from '../../../../hooks/useAuth'
import {
  authorizeSellerQueueAction,
  filterAdminQueueSellerOrders,
  filterTotvsConferenceOrders,
  getReconciliationData,
  getUsersByTypeRoute,
  hasTotvsIntegrationForConference,
} from '../services'
import {
  ConferenceOrderFilterPayload,
  ConferenceOrderItem,
  ConferenceSeller,
  ReconciliationOrderCandidate,
  ReconciliationRow,
  ReconciliationSessionItem,
  ReconciliationStatus,
} from '../types'

interface ReconciliationTabProps {
  isPrivileged: boolean
  pluginspaceId: number
}

const toUtcStartDate = (dateValue: string): string => `${dateValue}T00:00:00.000Z`
const toUtcEndDate = (dateValue: string): string => `${dateValue}T23:59:59.999Z`

const normalizeCpf = (value: string): string => value.replace(/\D/g, '')

const normalizeCode = (value: string): string =>
  value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')

const toObject = (value: unknown): Record<string, unknown> => {
  if (!value) return {}
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
    } catch {
      return {}
    }
  }
  if (typeof value === 'object') {
    return value as Record<string, unknown>
  }
  return {}
}

const pickContentValue = (content: Record<string, unknown>, keys: string[]): string => {
  for (const key of keys) {
    const raw = content[key]
    if (typeof raw === 'string' && raw.trim()) return raw
  }
  return ''
}

const extractOrderSellerFields = (order: ConferenceOrderItem): { cpf: string; code: string } => {
  const content = toObject(order.content)
  return {
    cpf: pickContentValue(content, ['sellerCpf', 'cpfSeller', 'sellerCpfCnpj', 'cpfVendedor']),
    code: pickContentValue(content, ['codeSeller', 'sellerCode', 'codSeller']),
  }
}

const getOrderStatus = (order: ConferenceOrderItem): string =>
  order.statusOrderType || order.statusLabel || order.status?.[0]?.name || '-'

const formatCurrency = (valueInCents: number | null): string => {
  if (valueInCents === null || Number.isNaN(valueInCents)) return '-'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(valueInCents / 100)
}

const formatDateTime = (value: string | null): string => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('pt-BR')
}

const statusLabel = (status: ReconciliationStatus): string => {
  switch (status) {
    case 'matched':
      return 'Compatível'
    case 'out_of_range':
      return 'Fora da faixa'
    case 'order_without_sale':
      return 'Pedido sem venda declarada'
    case 'sale_without_order':
      return 'Venda sem pedido correspondente'
    default:
      return '-'
  }
}

const statusBadgeClass = (status: ReconciliationStatus): string => {
  switch (status) {
    case 'matched':
      return 'badge badge-light-success'
    case 'out_of_range':
      return 'badge badge-light-warning'
    default:
      return 'badge badge-light-danger'
  }
}

const getFileTimestamp = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  return `${year}${month}${day}-${hours}${minutes}${seconds}`
}

export const ReconciliationTab: FC<ReconciliationTabProps> = ({ isPrivileged, pluginspaceId }) => {
  const { loginInfo } = useAuth()
  const currentUserId = Number(loginInfo?.id) || 0

  const [loading, setLoading] = useState(false)
  const [startDate, setStartDate] = useState('')
  const [finalDate, setFinalDate] = useState('')
  const [searchSource, setSearchSource] = useState<'ninegrid' | 'totvs' | 'hybrid'>('hybrid')
  const [sellers, setSellers] = useState<ConferenceSeller[]>([])
  const [selectedSellerId, setSelectedSellerId] = useState<number | null>(null)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)
  const [hasTotvsIntegration, setHasTotvsIntegration] = useState(false)
  const [statusFilter, setStatusFilter] = useState<ReconciliationStatus | 'all'>('all')

  const [proximityPercentage, setProximityPercentage] = useState(2)
  const [analysisInterval, setAnalysisInterval] = useState(10)
  const [loadingReconciliationConfig, setLoadingReconciliationConfig] = useState(true)
  const [reconciliationConfigError, setReconciliationConfigError] = useState(false)
  const [rows, setRows] = useState<ReconciliationRow[]>([])
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const pageSizeOptions = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100]

  const toggleRowExpanded = (key: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev)
      if (next.has(key)) {
        next.delete(key)
      } else {
        next.add(key)
      }
      return next
    })
  }

  useEffect(() => {
    const today = new Date()
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000)
    setStartDate(yesterday.toISOString().split('T')[0])
    setFinalDate(today.toISOString().split('T')[0])
  }, [])

  useEffect(() => {
    if (!startDate || !finalDate || !pluginspaceId) return

    let active = true
    const loadReconciliationConfig = async () => {
      setLoadingReconciliationConfig(true)
      setReconciliationConfigError(false)
      try {
        const data = await getReconciliationData(
          toUtcStartDate(startDate),
          toUtcEndDate(finalDate)
        )
        if (!active) return
        setProximityPercentage(data.proximityPercentage)
        setAnalysisInterval(data.analysisInterval)
      } catch {
        if (!active) return
        setReconciliationConfigError(true)
      } finally {
        if (active) setLoadingReconciliationConfig(false)
      }
    }

    void loadReconciliationConfig()
    return () => {
      active = false
    }
  }, [startDate, finalDate, pluginspaceId])

  useEffect(() => {
    const loadSellers = async () => {
      try {
        const result = await getUsersByTypeRoute('seller')
        if (isPrivileged) {
          setSellers(result)
          return
        }
        const scopedSellers = result.filter((seller) => seller.id === currentUserId)
        setSellers(scopedSellers)
        if (scopedSellers.length === 1) {
          setSelectedSellerId(scopedSellers[0].id)
        }
      } catch (error) {
        Swal.fire('Erro', 'Não foi possível carregar vendedores da loja.', 'error')
      }
    }
    loadSellers()
  }, [currentUserId, isPrivileged])

  useEffect(() => {
    const checkTotvsIntegration = async () => {
      const hasIntegration = await hasTotvsIntegrationForConference()
      setHasTotvsIntegration(hasIntegration)
      if (!hasIntegration) setSearchSource('ninegrid')
    }
    checkTotvsIntegration()
  }, [])

  const selectedSeller = useMemo(
    () => sellers.find((seller) => seller.id === selectedSellerId) || null,
    [sellers, selectedSellerId]
  )

  const validateFilter = (): boolean => {
    if (!startDate || !finalDate) {
      Swal.fire('Atenção', 'Preencha Data Início e Data Final.', 'warning')
      return false
    }
    if (new Date(startDate) > new Date(finalDate)) {
      Swal.fire('Atenção', 'Data Início não pode ser maior que Data Final.', 'warning')
      return false
    }
    if (!selectedSeller && !isPrivileged) {
      Swal.fire(
        'Permissão negada',
        'Sem vendedor selecionado, o filtro é permitido apenas para admin, gerente ou owner.',
        'error'
      )
      return false
    }
    if (searchSource === 'totvs' && !hasTotvsIntegration) {
      Swal.fire('Atenção', 'A integração TOTVS não está disponível para esta loja.', 'warning')
      return false
    }
    return true
  }

  const openAuthorization = () => {
    if (!validateFilter()) return
    setIsPasswordModalOpen(true)
  }

  // Compara pedidos com o valor declarado por unidade: participante multi-cliente ou sessão legada.
  // Cada venda pode listar até 3 pedidos candidatos (não exclusivos); um pedido só vira "sem venda"
  // quando não aparece como candidato de nenhuma venda.
  const buildReconciliationRows = (
    orders: ConferenceOrderItem[],
    sessions: ReconciliationSessionItem[],
    proximityPct: number,
    intervalMinutes: number
  ): ReconciliationRow[] => {
    const intervalMs = intervalMinutes * 60 * 1000
    const claimedOrderKeys = new Set<string>()
    const result: ReconciliationRow[] = []

    for (const session of sessions) {
      const sessionCpf = session.sellerCpf ? normalizeCpf(session.sellerCpf) : ''
      const sessionCode = session.sellerCode ? normalizeCode(session.sellerCode) : ''
      const sessionDate = new Date(session.finishedAt || session.startedAt).getTime()
      const saleAmountCents = Math.round(session.saleAmount * 100)
      const sellerIdentity = session.sellerCode || session.sellerName || session.sellerCpf || '-'
      const sellerLabel = `${sellerIdentity} - Cliente ${session.customerSequenceNumber || 1}`
      const sellerCode = session.sellerCode || '-'
      const sellerName = session.sellerName || '-'

      const candidates = orders
        .map((order) => {
          const { cpf, code } = extractOrderSellerFields(order)
          const orderCpf = cpf ? normalizeCpf(cpf) : ''
          const orderCode = code ? normalizeCode(code) : ''
          const sameSeller =
            (sessionCpf && orderCpf && sessionCpf === orderCpf) ||
            (sessionCode && orderCode && sessionCode === orderCode)
          if (!sameSeller) return null

          const orderDate = new Date(order.created).getTime()
          if (Number.isNaN(orderDate) || Number.isNaN(sessionDate)) return null

          const timeDiff = Math.abs(orderDate - sessionDate)
          if (timeDiff > intervalMs) return null

          const orderTotal = typeof order.total === 'number' ? order.total : null
          const differencePercentage =
            orderTotal && orderTotal > 0
              ? Number((((saleAmountCents - orderTotal) / orderTotal) * 100).toFixed(2))
              : null
          const withinRange =
            differencePercentage !== null ? Math.abs(differencePercentage) <= proximityPct : false

          return { order, timeDiff, orderTotal, differencePercentage, withinRange }
        })
        .filter((candidate): candidate is NonNullable<typeof candidate> => candidate !== null)
        .sort((a, b) => {
          if (a.withinRange !== b.withinRange) return a.withinRange ? -1 : 1
          return a.timeDiff - b.timeDiff
        })
        .slice(0, 3)

      candidates.forEach((candidate) => claimedOrderKeys.add(`order-${String(candidate.order.id)}`))

      if (candidates.length === 0) {
        result.push({
          key: `session-${session.sessionId}-customer-${session.sessionCustomerId || 'legacy'}`,
          status: 'sale_without_order',
          orderId: null,
          orderSource: null,
          orderTotal: null,
          orderDate: null,
          orderStatus: '-',
          sellerCode,
          sellerName,
          sellerLabel,
          saleAmount: saleAmountCents,
          saleDate: session.finishedAt || session.startedAt,
          differencePercentage: null,
          alternateOrders: [],
        })
        continue
      }

      const [primary, ...rest] = candidates
      const alternateOrders: ReconciliationOrderCandidate[] = rest.map((candidate) => ({
        orderId: candidate.order.orderidExt || candidate.order.id,
        orderSource: candidate.order.source || 'ninegrid',
        orderTotal: candidate.orderTotal,
        orderDate: candidate.order.created,
        orderStatus: getOrderStatus(candidate.order),
        differencePercentage: candidate.differencePercentage,
        withinRange: candidate.withinRange,
      }))

      result.push({
        key: `session-${session.sessionId}-customer-${session.sessionCustomerId || 'legacy'}`,
        status: primary.withinRange ? 'matched' : 'out_of_range',
        orderId: primary.order.orderidExt || primary.order.id,
        orderSource: primary.order.source || 'ninegrid',
        orderTotal: primary.orderTotal,
        orderDate: primary.order.created,
        orderStatus: getOrderStatus(primary.order),
        sellerCode,
        sellerName,
        sellerLabel,
        saleAmount: saleAmountCents,
        saleDate: session.finishedAt || session.startedAt,
        differencePercentage: primary.differencePercentage,
        alternateOrders,
      })
    }

    for (const order of orders) {
      const orderKey = `order-${String(order.id)}`
      if (claimedOrderKeys.has(orderKey)) continue

      const { cpf, code } = extractOrderSellerFields(order)
      const normalizedOrderCpf = cpf ? normalizeCpf(cpf) : ''
      const normalizedOrderCode = code ? normalizeCode(code) : ''
      const orderSeller = sellers.find((seller) => {
        const sellerCpf = seller.cpf ? normalizeCpf(seller.cpf) : ''
        const sellerCode = seller.sellerCode ? normalizeCode(seller.sellerCode) : ''
        return (
          (normalizedOrderCpf && sellerCpf === normalizedOrderCpf) ||
          (normalizedOrderCode && sellerCode === normalizedOrderCode)
        )
      })
      result.push({
        key: orderKey,
        status: 'order_without_sale',
        orderId: order.orderidExt || order.id,
        orderSource: order.source || 'ninegrid',
        orderTotal: typeof order.total === 'number' ? order.total : null,
        orderDate: order.created,
        orderStatus: getOrderStatus(order),
        sellerCode: orderSeller?.sellerCode || code || '-',
        sellerName: orderSeller?.name || '-',
        sellerLabel: code || cpf || '-',
        saleAmount: null,
        saleDate: null,
        differencePercentage: null,
        alternateOrders: [],
      })
    }

    return result.sort((a, b) => {
      const aDate = new Date(a.orderDate || a.saleDate || 0).getTime()
      const bDate = new Date(b.orderDate || b.saleDate || 0).getTime()
      return bDate - aDate
    })
  }

  const applyFilter = async (credential: string) => {
    const actionType = selectedSeller ? 'conference_search_seller' : 'conference_search_all'
    const auth = await authorizeSellerQueueAction(actionType, credential, selectedSeller?.id)

    if (!auth.authorized) {
      Swal.fire('Permissão negada', auth.message || 'Credencial sem permissão.', 'error')
      return
    }

    const basePayload: ConferenceOrderFilterPayload = {
      startDate: toUtcStartDate(startDate),
      finalDate: toUtcEndDate(finalDate),
      ...(selectedSeller?.cpf ? { cpfSeller: selectedSeller.cpf } : {}),
      ...(selectedSeller?.sellerCode ? { codeSeller: selectedSeller.sellerCode } : {}),
    }

    setLoading(true)
    try {
      const [reconciliationData, ninegridOrders, totvsResult] = await Promise.all([
        getReconciliationData(basePayload.startDate, basePayload.finalDate),
        searchSource !== 'totvs' ? filterAdminQueueSellerOrders(basePayload) : Promise.resolve([]),
        searchSource !== 'ninegrid' && hasTotvsIntegration
          ? filterTotvsConferenceOrders(basePayload, 1, 500)
          : Promise.resolve({ items: [], meta: { page: 1, pageSize: 500, totalItems: 0, totalPages: 1, hasNextPage: false } }),
      ])

      setProximityPercentage(reconciliationData.proximityPercentage)
      setAnalysisInterval(reconciliationData.analysisInterval)

      const sessions = selectedSeller
        ? reconciliationData.sessions.filter((session) => session.sellerId === selectedSeller.id)
        : reconciliationData.sessions

      const orders = [...ninegridOrders, ...totvsResult.items]

      setExpandedRows(new Set())
      setRows(
        buildReconciliationRows(
          orders,
          sessions,
          reconciliationData.proximityPercentage,
          reconciliationData.analysisInterval
        )
      )
    } catch (error) {
      const message = typeof error === 'string' ? error : 'Erro ao carregar a conciliação de pedidos.'
      Swal.fire('Erro', message, 'error')
    } finally {
      setLoading(false)
    }
  }

  const filteredRows = useMemo(
    () => (statusFilter === 'all' ? rows : rows.filter((row) => row.status === statusFilter)),
    [rows, statusFilter]
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, pageSize, filteredRows.length])

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize))
  const pagedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredRows.slice(startIndex, startIndex + pageSize)
  }, [filteredRows, currentPage, pageSize])

  const summary = useMemo(() => {
    const matched = rows.filter((row) => row.status === 'matched').length
    const outOfRange = rows.filter((row) => row.status === 'out_of_range').length
    const orderWithoutSale = rows.filter((row) => row.status === 'order_without_sale').length
    const saleWithoutOrder = rows.filter((row) => row.status === 'sale_without_order').length
    return { matched, outOfRange, orderWithoutSale, saleWithoutOrder }
  }, [rows])

  const handleExportExcel = () => {
    if (filteredRows.length === 0) return
    const sheetRows = filteredRows.map((row) => ({
      'N. Pedido': row.orderId ?? '-',
      Origem: row.orderSource || '-',
      'Código do Vendedor': row.sellerCode,
      Vendedor: row.sellerName,
      'Vlr. Decl. Venda': formatCurrency(row.saleAmount),
      'Pedido Totvs/Ninegrid': formatCurrency(row.orderTotal),
      'Diferença %': row.differencePercentage != null ? `${row.differencePercentage}%` : '-',
      'Período do Atendimento': formatDateTime(row.saleDate),
      'Período do Pedido': formatDateTime(row.orderDate),
      'Tipo Status': row.orderStatus,
      Classificação: statusLabel(row.status),
      'Outras Possibilidades': row.alternateOrders.length,
    }))
    const ws = XLSX.utils.json_to_sheet(sheetRows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'ConciliacaoPedidos')
    XLSX.writeFile(wb, `seller-queue-conciliacao-ps-${pluginspaceId}-${getFileTimestamp()}.xlsx`)
  }

  const handleExportPdf = () => {
    if (filteredRows.length === 0) return
    try {
      const doc = new jsPDF({ orientation: 'landscape' })
      const jsPDFAutoTable = require('jspdf-autotable')

      doc.setFontSize(14)
      doc.text('Conciliacao de Pedidos', 14, 15)
      doc.setFontSize(10)
      doc.text(`Periodo: ${startDate} ate ${finalDate}`, 14, 22)

      jsPDFAutoTable.autoTable(doc, {
        startY: 28,
        head: [['Id Order', 'Código', 'Vendedor', 'Vlr Declarado', 'Pedido', 'Dif %', 'Período Atendimento', 'Período Pedido', 'Tipo Status', 'Classificação', 'Outras Poss.']],
        body: filteredRows.map((row) => [
          String(row.orderId ?? '-'),
          row.sellerCode,
          row.sellerName,
          formatCurrency(row.saleAmount),
          formatCurrency(row.orderTotal),
          row.differencePercentage != null ? `${row.differencePercentage}%` : '-',
          formatDateTime(row.saleDate),
          formatDateTime(row.orderDate),
          row.orderStatus,
          statusLabel(row.status),
          String(row.alternateOrders.length),
        ]),
        theme: 'grid',
        headStyles: { fillColor: [13, 110, 253], textColor: 255 },
      })

      doc.save(`seller-queue-conciliacao-ps-${pluginspaceId}-${getFileTimestamp()}.pdf`)
    } catch (error) {
      console.error('Erro ao exportar PDF da conciliação:', error)
    }
  }

  return (
    <div className='position-relative'>
      <ConferenceLoadingOverlay visible={loading} />

      <div className='alert alert-light d-flex flex-wrap gap-6 align-items-center py-3 mb-4'>
        <span className='text-gray-700'>
          Faixa de proximidade:{' '}
          <strong>{loadingReconciliationConfig ? 'Carregando...' : `±${proximityPercentage}%`}</strong>
        </span>
        <span className='text-gray-700'>
          Janela de comparação:{' '}
          <strong>{loadingReconciliationConfig ? 'Carregando...' : `${analysisInterval} min`}</strong>
        </span>
        {reconciliationConfigError && (
          <span className='text-danger fs-7'>Não foi possível carregar a configuração.</span>
        )}
      </div>

      <div className='row g-4 mb-4'>
        <div className='col-md-3'>
          <label className='form-label'>Data Início</label>
          <input
            type='date'
            className='form-control'
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className='col-md-3'>
          <label className='form-label'>Data Final</label>
          <input
            type='date'
            className='form-control'
            value={finalDate}
            onChange={(e) => setFinalDate(e.target.value)}
          />
        </div>
        <div className='col-md-3'>
          <label className='form-label'>Vendedor</label>
          <select
            className='form-select'
            value={selectedSellerId ?? ''}
            onChange={(e) => setSelectedSellerId(e.target.value ? Number(e.target.value) : null)}
            disabled={!isPrivileged && sellers.length <= 1}
          >
            <option value=''>Todos os vendedores</option>
            {sellers.map((seller) => (
              <option key={seller.id} value={seller.id}>
                {seller.name}
              </option>
            ))}
          </select>
        </div>
        <div className='col-md-3'>
          <label className='form-label'>Origem do Pedido</label>
          <select
            className='form-select'
            value={searchSource}
            onChange={(e) => setSearchSource(e.target.value as 'ninegrid' | 'totvs' | 'hybrid')}
          >
            <option value='hybrid'>Híbrido (Ninegrid + TOTVS)</option>
            <option value='ninegrid'>Apenas Ninegrid</option>
            <option value='totvs' disabled={!hasTotvsIntegration}>
              Apenas TOTVS
            </option>
          </select>
        </div>
      </div>

      <div className='d-flex flex-wrap gap-3 mb-4'>
        <button className='btn btn-primary' onClick={openAuthorization} disabled={loading}>
          <i className='fas fa-search me-2'></i>
          Buscar Conciliação
        </button>
        <button className='btn btn-light-success' onClick={handleExportExcel} disabled={filteredRows.length === 0}>
          <i className='fas fa-file-excel me-2'></i>
          Exportar Excel
        </button>
        <button className='btn btn-light-danger' onClick={handleExportPdf} disabled={filteredRows.length === 0}>
          <i className='fas fa-file-pdf me-2'></i>
          Exportar PDF
        </button>
      </div>

      <div className='row g-4 mb-4'>
        <div className='col-md-3'>
          <button
            type='button'
            className={`card w-100 text-start border-0 ${statusFilter === 'matched' ? 'border border-success' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'matched' ? 'all' : 'matched')}
          >
            <div className='card-body py-4'>
              <div className='fs-2 fw-bold text-success'>{summary.matched}</div>
              <div className='fs-7 text-gray-700'>Compatíveis</div>
            </div>
          </button>
        </div>
        <div className='col-md-3'>
          <button
            type='button'
            className={`card w-100 text-start border-0 ${statusFilter === 'out_of_range' ? 'border border-warning' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'out_of_range' ? 'all' : 'out_of_range')}
          >
            <div className='card-body py-4'>
              <div className='fs-2 fw-bold text-warning'>{summary.outOfRange}</div>
              <div className='fs-7 text-gray-700'>Fora da faixa de proximidade</div>
            </div>
          </button>
        </div>
        <div className='col-md-3'>
          <button
            type='button'
            className={`card w-100 text-start border-0 ${statusFilter === 'order_without_sale' ? 'border border-danger' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'order_without_sale' ? 'all' : 'order_without_sale')}
          >
            <div className='card-body py-4'>
              <div className='fs-2 fw-bold text-danger'>{summary.orderWithoutSale}</div>
              <div className='fs-7 text-gray-700'>Pedido → sem Resultado do atendimento</div>
            </div>
          </button>
        </div>
        <div className='col-md-3'>
          <button
            type='button'
            className={`card w-100 text-start border-0 ${statusFilter === 'sale_without_order' ? 'border border-danger' : ''}`}
            onClick={() => setStatusFilter(statusFilter === 'sale_without_order' ? 'all' : 'sale_without_order')}
          >
            <div className='card-body py-4'>
              <div className='fs-2 fw-bold text-danger'>{summary.saleWithoutOrder}</div>
              <div className='fs-7 text-gray-700'>Resultado do atendimento → sem Pedido</div>
            </div>
          </button>
        </div>
      </div>

      <div className='d-flex justify-content-between align-items-center mb-3'>
        <span className='text-muted fs-7'>
          {filteredRows.length} registro(s) encontrado(s)
        </span>
        <div className='d-flex align-items-center gap-2'>
          <span className='text-muted fs-7'>Itens por página</span>
          <select
            className='form-select form-select-sm'
            style={{ width: 90 }}
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {pageSizeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className='table-responsive'>
        <table className='table table-row-dashed table-row-gray-300 gy-7'>
          <thead>
            <tr className='fw-bold fs-6 text-gray-800'>
              <th>N. Pedido</th>
              <th>Código</th>
              <th>Vendedor</th>
              <th>Vlr. Decl. Venda</th>
              <th>Pedido Totvs/Ninegrid</th>
              <th>Diferença %</th>
              <th>Período do Atendimento</th>
              <th>Período do Pedido</th>
              <th>Tipo Status</th>
              <th>Classificação</th>
              <th>Outras Possibilidades</th>
            </tr>
          </thead>
          <tbody>
            {pagedRows.length === 0 ? (
              <tr>
                <td colSpan={11} className='text-center py-5'>
                  Nenhum registro encontrado. Ajuste os filtros e clique em Buscar Conciliação.
                </td>
              </tr>
            ) : (
              pagedRows.map((row) => {
                const isExpanded = expandedRows.has(row.key)
                return (
                  <Fragment key={row.key}>
                    <tr>
                      <td>{row.orderId ?? '-'}</td>
                      <td>
                        <span className='badge badge-light-primary fw-semibold'>
                          {row.sellerCode}
                        </span>
                      </td>
                      <td className='fw-bold text-dark'>{row.sellerName}</td>
                      <td>{formatCurrency(row.saleAmount)}</td>
                      <td>{formatCurrency(row.orderTotal)}</td>
                      <td>{row.differencePercentage != null ? `${row.differencePercentage}%` : '-'}</td>
                      <td>
                        <small>{formatDateTime(row.saleDate)}</small>
                      </td>
                      <td>
                        <small>{formatDateTime(row.orderDate)}</small>
                      </td>
                      <td>{row.orderStatus}</td>
                      <td>
                        <span className={statusBadgeClass(row.status)}>{statusLabel(row.status)}</span>
                      </td>
                      <td>
                        {row.alternateOrders.length === 0 ? (
                          '-'
                        ) : (
                          <button
                            type='button'
                            className='btn btn-sm btn-light-primary'
                            onClick={() => toggleRowExpanded(row.key)}
                          >
                            {isExpanded ? 'Ocultar' : `Ver mais (${row.alternateOrders.length})`}
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded &&
                      row.alternateOrders.map((candidate, index) => (
                        <tr key={`${row.key}-alt-${index}`} className='text-muted'>
                          <td>{candidate.orderId}</td>
                          <td>
                            <span className='badge badge-light-primary fw-semibold'>
                              {row.sellerCode}
                            </span>
                          </td>
                          <td>{row.sellerName}</td>
                          <td>{formatCurrency(row.saleAmount)}</td>
                          <td>{formatCurrency(candidate.orderTotal)}</td>
                          <td>
                            {candidate.differencePercentage != null
                              ? `${candidate.differencePercentage}%`
                              : '-'}
                          </td>
                          <td>
                            <small>{formatDateTime(row.saleDate)}</small>
                          </td>
                          <td>
                            <small>{formatDateTime(candidate.orderDate)}</small>
                          </td>
                          <td>{candidate.orderStatus}</td>
                          <td>
                            <span
                              className={statusBadgeClass(
                                candidate.withinRange ? 'matched' : 'out_of_range'
                              )}
                            >
                              {statusLabel(candidate.withinRange ? 'matched' : 'out_of_range')}
                            </span>
                          </td>
                          <td>-</td>
                        </tr>
                      ))}
                  </Fragment>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {filteredRows.length > 0 && (
        <div className='d-flex justify-content-between align-items-center mt-4'>
          <div></div>

          <div className='text-muted fs-7'>
            Exibindo {(currentPage - 1) * pageSize + 1} -{' '}
            {Math.min(currentPage * pageSize, filteredRows.length)} de {filteredRows.length}
          </div>

          <div className='d-flex align-items-center gap-2'>
            <button
              className='btn btn-light btn-sm'
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Anterior
            </button>
            <span className='fw-semibold fs-7'>
              Página {currentPage} de {totalPages}
            </span>
            <button
              className='btn btn-light btn-sm'
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      <PasswordConfirmModal
        isOpen={isPasswordModalOpen}
        onConfirm={(password) => {
          setIsPasswordModalOpen(false)
          void applyFilter(password)
        }}
        onCancel={() => setIsPasswordModalOpen(false)}
        title='Confirmação de Permissão'
        description='Informe a senha para consultar a conciliação de pedidos:'
      />
    </div>
  )
}
